import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import usePageParams from 'hooks/usePageParams';
import useQueryParams from 'hooks/useQueryParams';
import { Loader } from 'ncoded-component-library';
import { DataRoomShareableLink } from 'models/ShareableLink';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import CreateShareableLinkProvider from 'router/subrouters/DataRoom/providers/CreateShareableLink';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import EditShareableLinkProvider from 'router/subrouters/DataRoom/providers/EditShareableLink';
import useRefetch from 'hooks/useRefetch';
import useUndoActions from 'hooks/useUndoActions';
import { getIsArrayEmpty, getSortValue, sentryError } from 'utils';
import ShareableLinkContent from '../../subrouters/ShareableLinks/pages/ShareableLinks/components/ShareableLinkContent';
import { SORTING_VALUES } from '../../subrouters/ShareableLinks/pages/ShareableLinks/components/ShareableLinksTable/components/data';
import { useLockScrollOnParamsReroute } from 'hooks/useLockScrollOnParamsReroute';
import useBusEvent from 'hooks/useBusEvent';
import { CallbackBusType, StandardEventTypes } from 'modules/bus';
import { useTranslation } from 'react-i18next';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import SegmentedToggle from 'components/SegmentedToggle';
import BannerWrapper from 'components/BannerWrapper';
import DiscoveryFeatureCard from 'components/DiscoveryFeatureCard';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { FIRST_PAGE, PAGE_PARAM_KEYWORD } from 'data/global';
import useTableFetchState from 'hooks/useTableFetchState';

import './ShareableLinksContentContainer.styles.scss';

const LIMIT = '10';

type ShareableLinksContentContainerProps = {
  className?: string;
  showIsActiveToggle?: boolean;
};

const ShareableLinksContentContainer: React.FC<
  ShareableLinksContentContainerProps
> = (props) => {
  const { children, className, showIsActiveToggle = false } = props;

  const [isActive, setIsActive] = useState(true);

  const {
    isLoading,
    totalPages,
    totalItems,
    data: shareableLinks,
    setIsLoading,
    setData,
    setPaginatedData,
  } = useTableFetchState<DataRoomShareableLink>();

  const [lastToggledLink, setLastToggledLink] =
    useState<DataRoomShareableLink | null>(null);

  const {
    dataRoom: { _id: dataRoomId, hasSecureLinkActivity },
  } = useContext(DataRoomContext);
  const {
    currentUser: { seenSecureLinkAnalyticsFeature },
    updatePreferences,
  } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const { refetch, refetchFlag } = useRefetch();

  const page = usePageParams();

  const { params, setQueryParam } = useQueryParams();

  const { sort } = params;

  useLockScrollOnParamsReroute();

  const isEmpty = useMemo(
    () => getIsArrayEmpty(shareableLinks),
    [shareableLinks],
  );

  const showDiscoveryBanner = useMemo(
    () => !isEmpty && !seenSecureLinkAnalyticsFeature && hasSecureLinkActivity,
    [hasSecureLinkActivity, isEmpty, seenSecureLinkAnalyticsFeature],
  );

  const classes = classNames('shareable-links-content-container', className);

  const { t } = useTranslation();

  const updateShareableLink = useCallback(
    (shareableLink: DataRoomShareableLink) =>
      setData((old) =>
        old.map((link) =>
          link?._id === shareableLink?._id ? shareableLink : link,
        ),
      ),
    [setData],
  );

  const handleIsActiveChange = useCallback(
    (isActive: boolean) => {
      setIsActive(isActive);
      setQueryParam(PAGE_PARAM_KEYWORD, FIRST_PAGE);
    },
    [setQueryParam],
  );

  const handleAfterToggleState = useCallback(
    (linkBeforeToggle: DataRoomShareableLink) => {
      setLastToggledLink(linkBeforeToggle);
      handleIsActiveChange(!linkBeforeToggle.isActive);
      setUndoTimeout(() => setLastToggledLink(null));
    },
    [handleIsActiveChange, setUndoTimeout],
  );

  const handleUndo = useCallback(async () => {
    if (!lastToggledLink) return;

    clearUndoTimeout();

    try {
      const { data } =
        await dataRoomApi.shareableLinks.changeShareableLinkState(
          lastToggledLink._id,
          lastToggledLink.isActive,
        );

      updateShareableLink({ ...lastToggledLink, ...data });
      refetch();
    } catch (e) {
      popServerError(e);
    } finally {
      setLastToggledLink(null);
    }
  }, [
    lastToggledLink,
    clearUndoTimeout,
    updateShareableLink,
    popServerError,
    refetch,
  ]);

  const updateShareableLinkFromEvent = useCallback(
    ({ payload }: CallbackBusType<DataRoomShareableLink>) => {
      if (!payload?._id) return;

      updateShareableLink(payload);
    },
    [updateShareableLink],
  );

  useBusEvent(
    StandardEventTypes.SHAREABLE_LINK_UPDATED,
    updateShareableLinkFromEvent,
  );

  useEffect(() => {
    if (sort) return;

    setQueryParam('sort', getSortValue(SORTING_VALUES.linkExpiresDate, false));
  }, [sort, setQueryParam]);

  useEffect(() => {
    if (!sort) return;

    (async () => {
      setIsLoading(true);
      try {
        const { data } = await dataRoomApi.shareableLinks.getShareableLinks(
          dataRoomId,
          {
            $page: page,
            $limit: LIMIT,
            $sort: sort,
            $relations: '["dataRoomNodes", "dataRoomNda"]',
            isActive,
          },
        );

        setPaginatedData(data);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    dataRoomId,
    page,
    sort,
    refetchFlag,
    isActive,
    setPaginatedData,
    setIsLoading,
  ]);

  if (!sort) return null;

  return (
    <>
      {showIsActiveToggle && (
        <SegmentedToggle
          options={[
            {
              label: t('active'),
              value: true,
            },
            {
              label: t('inactive'),
              value: false,
            },
          ]}
          selectedValue={isActive}
          onChange={(value) => handleIsActiveChange(!!value)}
        />
      )}
      {showDiscoveryBanner && (
        <BannerWrapper
          className="shareable-links-content-container__banner"
          onClose={() =>
            updatePreferences({ seenSecureLinkAnalyticsFeature: true })
          }
        >
          <DiscoveryFeatureCard
            title={t('secureLinkAnalyticsBannerTitle')}
            description="secureLinkAnalyticsBannerDescription"
          />
        </BannerWrapper>
      )}
      <div className={classes}>
        <CreateShareableLinkProvider onSuccessFullyCreated={refetch}>
          <EditShareableLinkProvider updateShareableLink={updateShareableLink}>
            {children && !isLoading && isEmpty ? (
              children
            ) : (
              <ShareableLinkContent
                isLoading={isLoading}
                isEmpty={isEmpty}
                isActive={isActive}
                totalPages={totalPages}
                totalItems={totalItems}
                shareableLinks={shareableLinks}
                updateShareableLink={updateShareableLink}
                onAfterToggleState={handleAfterToggleState}
              />
            )}
          </EditShareableLinkProvider>
        </CreateShareableLinkProvider>

        {isLoading && !isEmpty && <Loader />}
      </div>
      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        onUndo={handleUndo}
      >
        <p>
          {t(
            lastToggledLink?.isActive
              ? 'secureLinkInactiveMessage'
              : getIsArrayEmpty(lastToggledLink?.fileIds)
              ? 'secureLinkActiveMessage'
              : 'secureLinkWithFilesActiveMessage',
            { secureLink: lastToggledLink?.name },
          )}
        </p>
      </PopNotificationWithUndo>
    </>
  );
};

export default ShareableLinksContentContainer;
